// src/views/Support.jsx
import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  addDoc,
  collection,
  arrayUnion
} from 'firebase/firestore';
import {
  Container,
  Form,
  Button,
  Alert,
  Spinner,
  Fade
} from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import Spacer from './Spacer';

export default function Support() {
  const { user, loading: authLoading } = useAuth();
  const db = getFirestore();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [category, setCategory] = useState('general');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Prefill email for signed-in users
  useEffect(() => {
    if (!authLoading && user?.email) {
      setEmail(user.email);
    }
  }, [authLoading, user]);

  // Auto-dismiss success after 3s
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Auto-dismiss error after 3s
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const validate = () => {
    if (!email.trim()) return 'Email is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Invalid email.';
    if (message.trim().length < 10) return 'Message must be at least 10 characters.';
    if (category === 'child_safety' && message.trim().length < 20) return 'Please include enough detail for a child safety review.';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError('');
    setSubmitting(true);

    const base = {
      userId: user?.uid || null,
      email,
      source: 'webapp',
      path: location.pathname,
      category,
      childSafetyConcern: category === 'child_safety',
      priority: category === 'child_safety' ? 'high' : 'normal'
    };
    const entry = {
      category,
      childSafetyConcern: category === 'child_safety',
      message: message.trim(),
      createdAt: new Date()
    };

    try {
      if (user?.uid) {
        const ref = doc(db, 'supportRequests', user.uid);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          await updateDoc(ref, {
            requests: arrayUnion(entry)
          });
        } else {
          await setDoc(ref, {
            ...base,
            requests: [entry]
          });
        }
      } else {
        await addDoc(collection(db, 'supportRequests'), {
          ...base,
          requests: [entry]
        });
      }
      setSuccess(true);
      setMessage('');
    } catch (err) {
      console.error(err);
      setError('Failed to send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading) return null;

  return (
    <Container className="py-5">
      <Spacer />
      <Spacer />

      <h1 className="mb-4 text-center">Support</h1>

      {/* Success Alert */}
      <Fade in={success}>
        <div>
          {success && (
            <Alert
              variant="light"
              className="bg-transparent border border-2 border-white text-white"
            >
              <div className="fw-bold fs-5 mb-1">Thanks! Your message was sent.</div>
              <div className="small">
                {category === 'child_safety' ? 'Our safety contact will review this report and follow up at ' : 'We’ll follow up at '}
                <a
                  href={`mailto:${email}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-warning text-decoration-underline"
                >
                  {email}
                </a>.
              </div>
            </Alert>
          )}
        </div>
      </Fade>

      {/* Error Alert */}
      <Fade in={!!error}>
        <div>
          {error && (
            <Alert variant="danger" className="text-center">
              {error}
            </Alert>
          )}
        </div>
      </Fade>

      <Form onSubmit={handleSubmit}>
        {!user && (
          <Form.Group className="mb-3" controlId="supportEmail">
            <Form.Label>Your Email</Form.Label>
            <Form.Control
              className="bg-dark text-light"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Form.Group>
        )}

        <Form.Group className="mb-3" controlId="supportCategory">
          <Form.Label>What do you need help with?</Form.Label>
          <Form.Select
            className="bg-dark text-light"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="general">General support</option>
            <option value="account">Account or login help</option>
            <option value="safety">Report unsafe behavior</option>
            <option value="child_safety">Child safety / exploitation concern</option>
            <option value="business">Business, ads, or venue support</option>
          </Form.Select>
          {category === 'child_safety' && (
            <Form.Text className="text-warning">
              Use this option for urgent concerns involving minors, grooming, exploitation, CSAM, or suspected child safety risks.
            </Form.Text>
          )}
        </Form.Group>

        <Form.Group className="mb-3" controlId="supportMessage">
          <Form.Label>Your Message</Form.Label>
          <Form.Control
            className="bg-dark text-light"
            as="textarea"
            rows={5}
            placeholder={category === 'child_safety' ? 'Describe the child safety concern. Include usernames, links, dates, or locations inside the app if available.' : 'How can we help?'}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
          />
        </Form.Group>

        <div className="d-flex justify-content-center">
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? <Spinner animation="border" size="sm" /> : 'Send'}
          </Button>
        </div>
      </Form>
    </Container>
  );
}
